import React, { useState, useEffect, useRef } from 'react';
import {
  fetchTotals,
  DEFAULT_SYNC_INTERVAL_MS,
  SIM_INCREMENT_MIN,
  SIM_INCREMENT_MAX,
  SIM_INTERVAL_MIN_MS,
  SIM_INTERVAL_MAX_MS,
  MILESTONES,
  formatNumber,
  getCurrentMilestoneIndex,
  getMilestoneRange,
  getProgressPercentWithinRange
} from '../lib/stats';

const ProgressTracker = () => {
  // State
  const [serverCount, setServerCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [milestoneIndex, setMilestoneIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [pulse, setPulse] = useState(false);

  // Refs for intervals and flags
  const simulateTimerRef = useRef(null);
  const syncTimerRef = useRef(null);
  const rafRef = useRef(null);
  const isMountedRef = useRef(false);

  // Easing function for smooth animation
  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  // Helpers
  const clampPercentLabel = (pct) => (pct > 94 ? 94 : pct);

  const computeProgressFor = (value, index) => {
    const [start, end] = getMilestoneRange(index, MILESTONES);
    return getProgressPercentWithinRange(value, start, end);
  };

  const recomputeMilestoneIndex = (count) => {
    const idx = getCurrentMilestoneIndex(count, MILESTONES);
    setMilestoneIndex(idx);
    return idx;
  };

  const animateToValue = (fromValue, toValue, durationMs = 1200, onDone = null, progressIndex = milestoneIndex) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setIsAnimating(true);
    const startTs = Date.now();
    const startValue = fromValue;
    const delta = toValue - fromValue;

    const step = () => {
      const elapsed = Date.now() - startTs;
      const t = Math.min(elapsed / durationMs, 1);
      const eased = easeOutQuart(t);
      const newValue = startValue + delta * eased;
      setDisplayCount(newValue);
      const idx = progressIndex;
      const pct = computeProgressFor(newValue, idx);
      setProgress(pct);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setIsAnimating(false);
        if (typeof onDone === 'function') onDone();
      }
    };
    rafRef.current = requestAnimationFrame(step);
  };

  // Initial load: fetch totals and animate in
  useEffect(() => {
    isMountedRef.current = true;
    const load = async () => {
      const totals = await fetchTotals();
      if (!isMountedRef.current) return;
      const truth = Number(totals.reviews || 0);
      setServerCount(truth);
      const idx = recomputeMilestoneIndex(truth);
      const [start] = getMilestoneRange(idx, MILESTONES);
      // Animate from a small backoff within the same milestone (or 0)
      const startFrom = Math.max(start, truth - 500);
      setDisplayCount(startFrom);
      setProgress(computeProgressFor(startFrom, idx));
      animateToValue(startFrom, truth, 1400, () => {
        kickOffSimulation();
        startSyncLoop();
      }, idx);
    };
    load();
    return () => {
      isMountedRef.current = false;
      if (simulateTimerRef.current) clearTimeout(simulateTimerRef.current);
      if (syncTimerRef.current) clearInterval(syncTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Pulse effect toggler respecting reduced motion (CSS will guard)
  const triggerPulse = () => {
    setPulse(true);
    setTimeout(() => setPulse(false), 140);
  };

  // Pick a delay with a triangular distribution (more natural than uniform)
  const pickDelayMs = (min, max) => {
    const a = Math.random();
    const b = Math.random();
    const t = (a + b) / 2; // center-weighted
    return Math.round(min + t * (max - min));
  };

  const scheduleNextSimulation = () => {
    const delay = pickDelayMs(SIM_INTERVAL_MIN_MS, SIM_INTERVAL_MAX_MS);
    if (simulateTimerRef.current) clearTimeout(simulateTimerRef.current);
    simulateTimerRef.current = setTimeout(simulateTick, delay);
  };

  const simulateTick = () => {
    setDisplayCount(prev => {
      const inc = 1; // increment by 1 per request
      const candidate = prev + inc;
      const idx = getCurrentMilestoneIndex(serverCount, MILESTONES); // lock milestone to server truth
      const [start, end] = getMilestoneRange(idx, MILESTONES);
      // If simulation would exceed end while server hasn't reached it, cap to end-1 to avoid rollover illusion
      const capped = serverCount < end ? Math.min(candidate, end - 1) : candidate;
      const pct = getProgressPercentWithinRange(capped, start, end);
      setProgress(pct);
      triggerPulse();
      return capped;
    });
    scheduleNextSimulation();
  };

  const kickOffSimulation = () => {
    scheduleNextSimulation();
  };

  const startSyncLoop = () => {
    if (syncTimerRef.current) clearInterval(syncTimerRef.current);
    syncTimerRef.current = setInterval(async () => {
      const totals = await fetchTotals();
      if (!isMountedRef.current) return;
      const truth = Number(totals.reviews || 0);
      const prevTruth = serverCount;
      setServerCount(truth);

      // Determine milestone transitions based on server truth only
      const prevIdx = getCurrentMilestoneIndex(prevTruth, MILESTONES);
      const nextIdx = getCurrentMilestoneIndex(truth, MILESTONES);
      if (nextIdx !== prevIdx) {
        // Rollover: smoothly transition UI to new milestone context
        setMilestoneIndex(nextIdx);
      }

      // If display exceeded truth, animate back; otherwise nudge forward if truth advanced
      setDisplayCount(currentDisplay => {
        if (currentDisplay > truth) {
          const from = currentDisplay;
          triggerPulse();
          animateToValue(from, truth, 800);
          return currentDisplay; // actual state will be driven by animation frames
        }
        if (truth > currentDisplay) {
          const from = currentDisplay;
          triggerPulse();
          animateToValue(from, truth, 800);
          return currentDisplay;
        }
        return currentDisplay;
      });
    }, DEFAULT_SYNC_INTERVAL_MS);
  };

  // Recompute progress when milestone index changes (e.g., after server rollover)
  useEffect(() => {
    const [start, end] = getMilestoneRange(milestoneIndex, MILESTONES);
    setProgress(getProgressPercentWithinRange(displayCount, start, end));
  }, [milestoneIndex]);

  // Clamp percentage label position
  const clamped = clampPercentLabel(progress);
  const percentageLabelStyle =
    clamped >= 94
      ? { right: 6 }
      : { left: `calc(${clamped}% + 6px)` };

  const [rangeStart, rangeEnd] = getMilestoneRange(milestoneIndex, MILESTONES);

  return (
    <div className="w-full max-w-md mx-auto mb-8">
      {/* Counter Display */}
      <div className="text-center mb-4">
        <div className={`text-text-black section-heading-font ${pulse ? 'cs-pulse' : ''}`}>
          <span className="text-2xl md:text-3xl font-bold">{formatNumber(displayCount)}</span>
          <span className="ml-2 text-xl md:text-2xl font-normal text-gray-700">sandwiches reviewed</span>
        </div>
      </div>

      {/* Progress Bar Container */}
      <div className="flex items-center">
        {/* Left outside label - current milestone start */}
        <span className="mr-2 text-xs text-gray-500 mplus2-font">{formatNumber(rangeStart)}</span>

        {/* Background track with subtle tick marks */}
        <div className="relative flex-1 w-full h-6 bg-gray-200 rounded-full overflow-hidden">
          {/* Progress fill */}
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-200 ease-out overflow-hidden"
            style={{ 
              width: `${Math.max(0, Math.min(100, progress))}%`,
              background: 'linear-gradient(90deg, #ff4f2b 0%, #ffa72b 100%)',
              boxShadow: '0 1px 3px rgba(255, 79, 43, 0.3)',
              zIndex: 2
            }}
            role="progressbar"
            aria-valuenow={Math.round(getProgressPercentWithinRange(displayCount, rangeStart, rangeEnd))}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label={`Progress from ${formatNumber(rangeStart)} to ${formatNumber(rangeEnd)}: ${formatNumber(displayCount)} of ${formatNumber(rangeEnd)} (Current Milestone: ${formatNumber(rangeStart)}–${formatNumber(rangeEnd)})`}
          >
            {/* Subtle shimmer effect */}
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                transform: 'translateX(-100%)',
                animation: isAnimating ? 'shimmer 3s ease-in-out infinite' : 'none'
              }}
            />
          </div>
          
          {/* Tick marks within current milestone segment */}
          <div className="absolute inset-0 flex justify-between items-center px-1" style={{ zIndex: 3 }}>
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                className="w-px h-4 bg-gray-400 opacity-40"
                style={{ marginLeft: i === 0 ? '0' : '-0.5px' }}
              />
            ))}
          </div>
          
          {/* Percentage badge just to the right of the filled area (inside bar) */}
          <div 
            className="absolute text-xs font-medium"
            style={{ 
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#000',
              zIndex: 4,
              ...percentageLabelStyle
            }}
          >
            {Math.max(0, Math.min(100, progress)).toFixed(1)}%
          </div>
        </div>

        {/* Right outside label - current milestone end */}
        <span className="ml-2 text-xs text-gray-500 mplus2-font">{formatNumber(rangeEnd)}</span>
      </div>

      {/* Current Milestone Label */}
      <div className="text-center mt-2">
        <span className="text-xs sm:text-sm text-gray-600 mplus2-font">
          Current Milestone: {formatNumber(rangeStart)}–{formatNumber(rangeEnd)}
        </span>
      </div>
    </div>
  );
};

export default ProgressTracker;
